/**
 * Custom hook to debounce a function call.
 *
 * @param callback The function to debounce.
 * @param wait The number of milliseconds to delay.
 * @returns A debounced version of the provided function.
 *
 * This hook creates a debounced function that delays invoking the `callback` until after
 * `wait` milliseconds have elapsed since the last time the debounced function was invoked.
 * Useful for optimizing performance for operations like search input that don't need to
 * fire on every input change.
 *
 * Usage example:
 *
 * const debouncedSearch = useDebounce(searchFunction, 300);
 *
 * // `searchFunction` will be called 300ms after the last call to `debouncedSearch`.
 */

export function useDebounce<T extends (...args: any[]) => any>(
    callback: T,
    wait: number
) {
    let timeout: NodeJS.Timeout | null = null
    return function (...args: Parameters<T>) {
        const next = () => callback(...args)
        if (timeout) {
            clearTimeout(timeout)
        }
        timeout = setTimeout(next, wait)
    }
}
